import * as braze from "@braze/web-sdk";
import type { KadeweBrazeEvent, KadeweUser } from "~/types";

export default defineNuxtPlugin(() => {
    const {
        public: {
            braze: { api_key: apiKey, base_url: baseUrl, logging: enableLogging },
        },
    } = useRuntimeConfig();

    const user = useSanctumUser<KadeweUser>();
    const { refreshIdentity } = useSanctumAuth();
    const contentCards = reactive([]) as KadeweBrazeEvent[];
    let initialized = false;
    const pendingCalls: Array<() => void> = [];

    const runOrQueue = (fn: () => void) => {
        if (initialized) {
            fn();
        } else {
            pendingCalls.push(fn);
        }
    };

    // Order per Braze docs: changeUser and subscribeToContentCardsEvents before openSession,
    // otherwise content cards are not refreshed on session start.
    const startSession = (contactId: string) => {
        console.debug("Braze LP: Starting session");
        console.debug(`Braze LP: Unsubscribing from previous content card updates.`);
        braze.removeAllSubscriptions();

        braze.subscribeToSdkAuthenticationFailures(async (error) => {
            console.warn("Braze LP: SDK authentication failed, refreshing signature", error);
            const previousSignature = user.value?.braze_sdk_auth;
            await refreshIdentity();

            // Re-setting the same rejected token would just trigger the next failure (and refresh) again.
            const signature = user.value?.braze_sdk_auth;
            if (signature && signature !== previousSignature) {
                braze.setSdkAuthenticationSignature(signature);
            }
        });

        if (contactId) {
            console.debug(`Braze LP: Changing user to contact ID ${contactId}`);
            braze.changeUser(contactId, user.value?.braze_sdk_auth ?? undefined);
        }
        braze.subscribeToContentCardsEvents((event) => {
            if (event.type === braze.ChannelEventType.ERROR) {
                console.debug(`Braze LP: Content cards error: ${event.reason} (${event.retryState})`);
                return;
            }
            if (!("cacheSnapshot" in event)) {
                return;
            }

            const newContentCards = event.cacheSnapshot.contentCards;
            const count = newContentCards.cards.length;
            const unviewedCount = newContentCards.getUnviewedCardCount();
            const time = newContentCards.lastUpdated;
            console.debug(
                `Braze LP: Content cards updated (${event.type}): ${count} cards, ${unviewedCount} unviewed, last updated at ${time}`,
            );

            contentCards.splice(0, contentCards.length, ...(newContentCards.cards as KadeweBrazeEvent[]));
        });

        braze.openSession();
        console.debug(`Braze LP: Session has been created`);
    };

    watch(
        () => user.value?.contact_id,
        (newContactId, oldContactId) => {
            if (initialized && newContactId && newContactId !== oldContactId) {
                startSession(newContactId);
            }
        },
    );

    return {
        provide: {
            braze: {
                initialize: () => {
                    if (initialized) {
                        return;
                    }

                    if (!apiKey) {
                        console.warn("Braze API key is not set");
                        return;
                    }

                    braze.initialize(apiKey, {
                        baseUrl: baseUrl,
                        enableLogging: enableLogging,
                        openCardsInNewTab: true,
                        enableSdkAuthentication: true,
                        localization: user.value?.language,
                    });
                    initialized = true;

                    startSession(user.value?.contact_id ? user.value.contact_id : "");

                    pendingCalls.splice(0).forEach((fn) => fn());
                },
                logContentCardImpression(event: KadeweBrazeEvent) {
                    console.debug("Braze LP: Logging content card impression", event);
                    braze.logContentCardImpressions([event]);
                },
                logContentCardClick(event: KadeweBrazeEvent) {
                    console.debug("Braze LP: Logging content card click", event);
                    braze.logContentCardClick(event);
                },
                requestContentCardsRefresh: () => {
                    return new Promise<void>((resolve, reject) => {
                        runOrQueue(() => {
                            braze.requestContentCardsRefresh(
                                () => {
                                    console.debug("Content cards successfully refreshed.");
                                    resolve();
                                },
                                () => {
                                    // Since SDK v7 this fires right away while the server config is still
                                    // pending (first init); cards then arrive via the session-start refresh.
                                    console.warn("Braze LP: Content cards refresh failed or channel not enabled yet.");
                                    reject();
                                },
                            );
                        });
                    });
                },
                contentCards,
            },
        },
    };
});
